import { Box, Divider } from '@mui/material';
import { Provider, type BackupJob } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import {
  backupJobItemFields,
  backupJobSummaryFromFields,
  backupJobSummaryToFields,
  fsxBackupJobSummaryToFields,
  isNativeAwsBackupJob,
} from './backupJobItemFields';
import { JobItemFields } from './jobItemFields';
import { JobStatusCard } from './jobStatusCard';
import { OperationSummary } from './operationSummary';
import { StepsSummary } from './stepsSummary';

export const BackupJobsCard = ({
  job,
  onClose,
  onBack,
}: {
  job: BackupJob | string;
  onClose: () => void;
  onBack?: () => void;
}) => {
  return (
    <PanelWrapper
      header={{
        title: 'Job details',
        onClose,
        hideBorder: true,
        onBack,
      }}
      testIdPrefix='backup-jobs-card'
    >
      {typeof job === 'string' ? (
        <JobContentFromId jobId={job} onClose={onClose} />
      ) : (
        <BackupJobsCardContent job={job} />
      )}
    </PanelWrapper>
  );
};

const JobContentFromId = ({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) => {
  const { snackbar } = useWorkspace();
  const dal = useDAL();
  const { data: job, isLoading } = dal.jobs.backup.useJob(jobId);

  useEffect(() => {
    if (!isLoading && !job?.job) {
      onClose();
      snackbar.showWarningMessage(
        'Looks like your link is broken and this job ID is invalid.'
      );
    }
  }, [job, isLoading, onClose, snackbar]);

  return isLoading || !job?.job ? (
    <Loader />
  ) : (
    <BackupJobsCardContent job={job.job} />
  );
};

const BackupJobsCardContent = ({ job }: { job: BackupJob }) => {
  const fromCloudProvider = job.resourceDetails?.cloudProvider;
  const isNativeAwsBackup = isNativeAwsBackupJob(job);

  // For native AWS Backup jobs (FSx/EFS), the data is stored in the source account's AWS Backup Vault
  const toCloudProvider = isNativeAwsBackup
    ? Provider.Aws
    : job.vault?.vaultAttributes.cloudProvider;
  const toFields = isNativeAwsBackup
    ? fsxBackupJobSummaryToFields
    : backupJobSummaryToFields;

  return (
    <Box className='pb-[40px]' overflow='auto'>
      <JobItemFields job={job} fields={backupJobItemFields} />
      <Divider />
      <JobStatusCard job={job} />
      <OperationSummary
        job={job}
        fromTitle='Backup from'
        fromFields={backupJobSummaryFromFields}
        fromCloudProvider={fromCloudProvider}
        toTitle='Backup to'
        toFields={toFields}
        toCloudProvider={toCloudProvider}
      />
      <StepsSummary job={job} />
    </Box>
  );
};
